import React, { useState, useRef } from 'react';
import { productsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/components/Toast';
import { Upload, FileSpreadsheet, Loader2, ArrowLeft, CheckCircle2, Wand2 } from 'lucide-react';

const IGNORE = '';

export const ImportWizard = ({ open, onClose, onImported }) => {
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('select'); // select | map | done
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [hasHeader, setHasHeader] = useState(false);
  const [tipoVenda, setTipoVenda] = useState('unidade');
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep('select'); setFile(null); setPreview(null);
    setMapping({}); setHasHeader(false); setTipoVenda('unidade');
    setResult(null); setLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const loadPreview = async (selectedFile, forceHeader) => {
    setLoading(true);
    try {
      const { data } = await productsAPI.importPreview(selectedFile, forceHeader);
      setPreview(data);
      setHasHeader(data.has_header);
      const initial = {};
      (data.fields || []).forEach((f) => {
        initial[f.key] = data.suggested_mapping?.[f.key] ?? IGNORE;
      });
      setMapping(initial);
      setStep('map');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao ler o arquivo');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    await loadPreview(f);
  };

  const toggleHeader = async (checked) => {
    setHasHeader(checked);
    if (file) await loadPreview(file, checked);
  };

  const columnLabel = (idx) => {
    const col = preview?.columns?.find((c) => c.index === idx);
    if (!col) return `Coluna ${idx + 1}`;
    const ex = col.samples?.length ? ` — ex: ${col.samples.slice(0, 2).join(', ')}` : '';
    return `${col.label}${ex}`;
  };

  const handleImport = async () => {
    if (mapping.nome === IGNORE || mapping.nome === undefined) {
      toast.error('Selecione qual coluna é o Nome do produto');
      return;
    }
    setLoading(true);
    try {
      const cleanMap = {};
      Object.entries(mapping).forEach(([k, v]) => {
        if (v !== IGNORE && v !== undefined && v !== null) cleanMap[k] = Number(v);
      });
      const { data } = await productsAPI.importMapped(file, cleanMap, hasHeader, tipoVenda);
      setResult(data);
      setStep('done');
      if (data.imported > 0) {
        toast.success(`${data.imported} produtos importados!`);
        onImported?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            Importar / Migrar Produtos
          </DialogTitle>
          <DialogDescription>
            Traga os produtos do seu sistema antigo. Aceita Excel (.xlsx, .xls antigo) e CSV.
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1: SELECT */}
        {step === 'select' && (
          <div className="py-4">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="wizard-file-upload"
                disabled={loading}
                data-testid="import-wizard-file-input"
              />
              <label htmlFor="wizard-file-upload" className="cursor-pointer">
                {loading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                    <span className="text-slate-600">Lendo arquivo...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-10 h-10 text-slate-400" />
                    <span className="text-slate-700 font-medium">Clique para selecionar o arquivo</span>
                    <span className="text-sm text-slate-400">.xlsx, .xls ou .csv</span>
                  </div>
                )}
              </label>
            </div>
            <p className="text-sm text-slate-500 mt-4 text-center">
              No próximo passo você diz qual coluna é o Nome, Preço, NCM, etc.
            </p>
          </div>
        )}

        {/* STEP 2: MAP */}
        {step === 'map' && preview && (
          <div className="py-2 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <Wand2 className="w-4 h-4" />
                <span><strong>{preview.total_rows}</strong> produtos encontrados em <strong>{preview.columns.length}</strong> colunas</span>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasHeader}
                  onChange={(e) => toggleHeader(e.target.checked)}
                  data-testid="import-has-header-checkbox"
                />
                A primeira linha é o cabeçalho
              </label>
            </div>

            {/* Prévia da tabela */}
            {preview.sample_rows?.length > 0 && (
              <div className="border rounded-lg overflow-x-auto max-h-48">
                <table className="text-xs w-full">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      {preview.columns.map((c) => (
                        <th key={c.index} className="px-2 py-1 text-left font-medium text-slate-600 whitespace-nowrap border-r">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample_rows.map((row, ri) => (
                      <tr key={ri} className="border-t">
                        {preview.columns.map((c) => (
                          <td key={c.index} className="px-2 py-1 whitespace-nowrap border-r text-slate-700">
                            {String(row[c.index] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mapeamento */}
            <div>
              <h4 className="font-medium text-slate-800 mb-2">Relacione as colunas do seu arquivo</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {preview.fields.map((f) => (
                  <div key={f.key} className="flex flex-col gap-1">
                    <label className="text-sm text-slate-600">
                      {f.label} {f.required && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      value={mapping[f.key] ?? IGNORE}
                      onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}
                      className={`h-10 px-3 border rounded-md bg-white text-sm ${f.required && (mapping[f.key] === IGNORE) ? 'border-red-300' : 'border-slate-300'}`}
                      data-testid={`import-map-${f.key}`}
                    >
                      <option value={IGNORE}>— Ignorar —</option>
                      {preview.columns.map((c) => (
                        <option key={c.index} value={c.index}>{columnLabel(c.index)}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Tipo de venda padrão */}
            <div className="bg-slate-50 rounded-lg p-3">
              <label className="text-sm text-slate-600 block mb-1">
                Tipo de venda padrão (usado quando o produto não tem a coluna P/N preenchida)
              </label>
              <select
                value={tipoVenda}
                onChange={(e) => setTipoVenda(e.target.value)}
                className="h-10 px-3 border border-slate-300 rounded-md bg-white text-sm w-full sm:w-64"
                data-testid="import-tipo-venda-select"
              >
                <option value="unidade">Unidade</option>
                <option value="pesado">Pesado (kg)</option>
                <option value="fracionado">Fracionado</option>
              </select>
              <p className="text-xs text-slate-500 mt-2">
                Dica: se você mapear a coluna "Tipo de venda (P/N)", cada produto vira Pesado (P) ou Unidade (N) automaticamente.
              </p>
            </div>
          </div>
        )}

        {/* STEP 3: DONE */}
        {step === 'done' && result && (
          <div className="py-8 text-center space-y-3">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
            <p className="text-xl font-semibold text-slate-800" data-testid="import-result-count">
              {result.imported} produtos importados!
            </p>
            {result.errors > 0 && (
              <p className="text-sm text-amber-600">{result.errors} linha(s) com problema (ignoradas)</p>
            )}
            {result.error_details?.length > 0 && (
              <div className="text-xs text-red-500 max-h-28 overflow-y-auto text-left bg-red-50 rounded p-2 max-w-md mx-auto">
                {result.error_details.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 'map' && (
            <>
              <Button variant="outline" onClick={() => { reset(); }} data-testid="import-wizard-back">
                <ArrowLeft className="w-4 h-4 mr-2" /> Trocar arquivo
              </Button>
              <Button onClick={handleImport} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="import-wizard-confirm">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importando...</> : `Importar ${preview?.total_rows || ''} produtos`}
              </Button>
            </>
          )}
          {step === 'select' && (
            <Button variant="outline" onClick={handleClose} data-testid="import-wizard-close">Fechar</Button>
          )}
          {step === 'done' && (
            <Button onClick={handleClose} className="bg-slate-900 hover:bg-slate-800" data-testid="import-wizard-finish">Concluir</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
